import { Suspense, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccount } from 'wagmi';
import { cn } from '@/lib/utils';
import bitchillLogo from '@/assets/bitchill-logo.svg';
import { useIsNewUser } from '@/shared/hooks/useIsNewUser';
import { lazyWithChunkReload } from '@/shared/utils/lazyWithChunkReload';
import { ScheduleActionWatchers } from '@/features/dca-schedules/components/ScheduleActionWatchers';
import { Toaster } from '@/components/ui/sonner';
import { MaintenanceBanner } from '@/shared/components/MaintenanceBanner';

const WalletButton = lazyWithChunkReload(() =>
  import('@/features/wallet/components/WalletButton').then((m) => ({ default: m.WalletButton })),
);

export function Layout() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isConnected } = useAccount();
  const { isNew } = useIsNewUser();
  const location = useLocation();

  const isHomePage = location.pathname === '/';
  const showNav = isConnected && !isNew;

  const navItems = [
    { to: '/', label: t('nav.home') },
    { to: '/schedules', label: t('nav.strategies') },
    { to: '/history', label: t('nav.history') },
  ];

  return (
    <div className="flex min-h-screen flex-col">
      {/* Route-independent schedule tx tracking; must outlive page/modal unmounts. */}
      <ScheduleActionWatchers />
      {/* Longer than sonner's 4s default: these confirm money movement and
          should not be easy to miss. */}
      <Toaster duration={6000} />
      <MaintenanceBanner />
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-lg">
        <div className="mx-auto grid max-w-7xl grid-cols-3 items-center px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            {showNav && (
              <button
                className="rounded-md p-2 text-muted-foreground hover:bg-accent md:hidden"
                onClick={() => { setMobileMenuOpen(!mobileMenuOpen); }}
                aria-label={t('nav.toggleMenu')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  {mobileMenuOpen ? (
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  ) : (
                    <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  )}
                </svg>
              </button>
            )}
            {showNav && (
              <nav className="hidden items-center gap-1 md:flex">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      cn(
                        'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            )}
          </div>
          <div className="flex justify-center">
            {!isHomePage && (
              <NavLink to="/" className="flex items-center gap-2">
                <img src={bitchillLogo} alt="BitChill" className="h-8 w-auto" />
              </NavLink>
            )}
          </div>
          <div className="flex items-center justify-end">
            <Suspense fallback={<div className="h-10 w-36 animate-pulse rounded-lg bg-gray-800" />}>
              <WalletButton />
            </Suspense>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {showNav && mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => { setMobileMenuOpen(false); }}
            aria-hidden="true"
          />
          <nav className="fixed left-0 right-0 top-[57px] z-50 border-b border-border/40 bg-background px-4 py-2 md:hidden">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => { setMobileMenuOpen(false); }}
                className={({ isActive }) =>
                  cn(
                    'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </>
      )}
      <main className="flex-1 overflow-x-hidden px-4 py-8 sm:px-6">
        <Outlet />
      </main>
      <footer className="border-t border-border/40 py-4 text-center text-sm text-muted-foreground">
        {t('app.footer')}
      </footer>
    </div>
  );
}
